import {LoginWebResponse, PermissionFlags, UserPermission} from './auth.types';
import {PERMISSION_MODULE_ALIASES} from './landing-routes';

/** Login name that gets every permission, whatever its `Permission[]` holds. */
export const SUPER_ADMIN_USER_NAME = 'superadmin';

export function isSuperAdminUser(userName: string | null | undefined): boolean {
    return (userName ?? '').trim().toLowerCase() === SUPER_ADMIN_USER_NAME;
}

/**
 * Lookup key for a module name, so "Material Mapping", "MATERIAL_MAPPING" and "MaterialMapping"
 * all match. Backend names that differ from ours are mapped via PERMISSION_MODULE_ALIASES.
 */
export function moduleKey(name: string): string {
    const key = normalize(name);
    return ALIASES.get(key) ?? key;
}

const ALIASES = new Map(
    Object.entries(PERMISSION_MODULE_ALIASES).map(([from, to]) => [normalize(from), normalize(to)])
);

function normalize(name: string): string {
    return name.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

/**
 * Module named by a permission, e.g. "Projects Page - PMCC" / "Projects Activity - PMCC" → "Projects":
 * drops the " - PMCC" application suffix and the trailing "Page" / "Activity" word.
 */
export function permissionModule(permission: string | null | undefined): string {
    return (permission ?? '')
        .replace(/\s*-\s*PMCC\s*$/i, '')
        .replace(/\s+(page|activity|activities)\s*$/i, '')
        .trim();
}

/**
 * Builds module → CRUD flags from the login `Permission[]`:
 * - PermissionType 1 (View): view the module's pages.
 * - PermissionType 2 (Write): view, create, edit and delete in the module.
 */
export function buildPermissions(list: readonly UserPermission[]): Record<string, PermissionFlags> {
    const perms: Record<string, PermissionFlags> = {};
    for (const p of list) {
        const key = moduleKey(permissionModule(p.Permission));
        if (!key) continue;
        const flags = (perms[key] ??= {canView: false, canCreate: false, canUpdate: false, canDelete: false});
        const type = Number(p.PermissionType);
        if (type === 1) {
            flags.canView = true;
        } else if (type === 2) {
            flags.canView = flags.canCreate = flags.canUpdate = flags.canDelete = true;
        }
    }
    return perms;
}

/** Role names from `Role[]`, which may hold plain strings or role objects. */
export function parseRoles(roles: readonly unknown[] | null | undefined): string[] {
    return (roles ?? [])
        .map((r) => {
            if (typeof r === 'string') return r;
            const o = r as Record<string, unknown> | null;
            const name = o?.['RoleName'] ?? o?.['Role'] ?? o?.['Name'] ?? o?.['roleName'] ?? o?.['name'];
            return typeof name === 'string' ? name : '';
        })
        .filter(Boolean);
}

/**
 * Reads the LoginWeb body, tolerating a `{data: …}` wrapper and camelCase keys
 * (ASP.NET's default serializer), so the rest of the app only sees `LoginWebResponse`.
 */
export function normalizeLoginResponse(body: unknown): LoginWebResponse {
    const root = asRecord(body);
    const raw = asRecord(root['data'] ?? root['Data'] ?? root);
    const get = (key: string): unknown => raw[key] ?? raw[key.charAt(0).toLowerCase() + key.slice(1)];

    const permissions = (Array.isArray(get('Permission')) ? (get('Permission') as unknown[]) : []).map((item) => {
        const p = asRecord(item);
        const pick = (key: string) => p[key] ?? p[key.charAt(0).toLowerCase() + key.slice(1)];
        return {
            Permission: String(pick('Permission') ?? ''),
            PermissionID: pick('PermissionID') as UserPermission['PermissionID'],
            Code: String(pick('Code') ?? ''),
            PermissionType: Number(pick('PermissionType')) as UserPermission['PermissionType'],
            PageID: (pick('PageID') ?? null) as UserPermission['PageID'],
        };
    });

    return {
        FolderPath: (get('FolderPath') as string) ?? null,
        UserName: (get('UserName') as string) ?? '',
        S3Config: get('S3Config'),
        UserID: get('UserID') as LoginWebResponse['UserID'],
        AccessToken: (get('AccessToken') as string) ?? '',
        Designation: (get('Designation') as string) ?? null,
        EmployeeID: (get('EmployeeID') as LoginWebResponse['EmployeeID']) ?? null,
        Supervisor: (get('Supervisor') as string) ?? null,
        Name: (get('Name') as string) ?? null,
        SupervisorID: (get('SupervisorID') as LoginWebResponse['SupervisorID']) ?? null,
        CompanyID: (get('CompanyID') as LoginWebResponse['CompanyID']) ?? null,
        Role: Array.isArray(get('Role')) ? (get('Role') as unknown[]) : [],
        Permission: permissions,
        IsPMCCWebApplicable: [true, 'true', 'True', 1, '1'].includes(get('IsPMCCWebApplicable') as never),
    };
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}
